import { computed, watch } from "vue";
import * as Enum from "@/assets/js/enum";

export function useProvisionalOrder(record, store) {
  const getInitOrderDate = () => {
    return window.Common.Util.getYearMonthDayIsoFormat();
  };
  const getInitLoadingDate = () => {
    return window.Common.Util.getYearMonthDayIsoFormat(getNextBusinessDay());
  };
  const getInitUnloadingDate = () => {
    return window.Common.Util.getYearMonthDayIsoFormat(getNextBusinessDay());
  };

  const setInitDate = () => {
    if (record.value.orderDate === "") record.value.orderDate = getInitOrderDate();
    if (record.value.loadingDate === "") record.value.loadingDate = getInitLoadingDate();
    if (record.value.unloadingDate === "") record.value.unloadingDate = getInitUnloadingDate();
  };

  const getNextBusinessDay = () => {
    const date = new Date();
    if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2);
    } else {
      date.setDate(date.getDate() + 1);
    }
    return date;
  };

  const loadingBaseIdList = computed(() => {
    return record.value.loadingBaseList
      ?.filter(loading => loading.baseId != null)
      .map(loading => loading.baseId);
  });

  const unloadingBaseIdList = computed(() => {
    return record.value.unloadingBaseList
      ?.filter(unloading => unloading.baseId != null)
      .map(unloading => unloading.baseId);
  });

  const loadingBaseLimit = computed(() => {
    return record.value.loadingBaseList?.length >= 5;
  });

  const unloadingBaseLimit = computed(() => {
    return record.value.unloadingBaseList?.length >= 5;
  });

  const isDispatchedOrder = computed(() => {
    return (
      record.value.id && (record.value.hasDispatched || record.value.hasRelayed || record.value.hasSlide)
    );
  });

  const canDisplayBillingData = computed(() => {
    if (record.value.id === undefined) return false;

    if (canSelectBase.value === false) {
      return false;
    }

    return (
      record.value.id &&
      canSelectBase.value &&
      loadingBaseIdList.value.length > 0 &&
      unloadingBaseIdList.value.length > 0
    );
  });

  /**
   * カードが編集可能かチェック
   * 請求書発行済み、確定済みとかだと不可
   * @type {ComputedRef<boolean|*>}
   */
  const canEditData = computed(() => {
    if (!record.value.id) return true;

    return store.getters["finance/canOriginCardEdit"];
  });

  const isSettingKey = computed(() => {
    if (record.value.clientId === undefined && record.value.clientBranchId === undefined) {
      return false;
    }

    return (
      record.value.clientId &&
      record.value.clientBranchId &&
      loadingBaseIdList.value.length > 0 &&
      unloadingBaseIdList.value.length > 0
    );
  });

  /**
   * 積地・卸地がリセットすべき対象かどうか
   * @type {ComputedRef<boolean|*>}
   */
  const isResetBaseTarget = computed(() => {
    if (record.value.loadingBaseList === undefined && record.value.unloadingBaseList === undefined) {
      return false;
    }

    const loadingExists =
      record.value.loadingBaseList.find(value => {
        return value.baseId !== null;
      }) !== undefined;

    const unloadingExists =
      record.value.unloadingBaseList.find(value => {
        return value.baseId !== null;
      }) !== undefined;

    return loadingExists || unloadingExists;
  });

  const isLostOrderRegisterable = computed(() => {
    return record.value.provisionalOrderStatus !== Enum.ProvisionalOrderStatusType.LOST.code;
  });

  const isLostOrderCancelable = computed(() => {
    return record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.LOST.code;
  });

  const canEditProvisionalOrderCard = computed(() => {
    if (record.value.id === undefined) return true;
    return record.value.canEditProvisionalOrderCard === true;
  });

  const isDisplayChangeCreatingOrderStatus = computed(() => {
    if (record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.CREATING_ORDER.code) {
      return false;
    }

    if (record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.CONFIRMED.code) {
      return false;
    }

    if (record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.LOST.code) {
      return false;
    }
    return true;
  });

  const isDisplayCancelCreatingOrderStatus = computed(() => {
    return record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.CREATING_ORDER.code;
  });

  const canDeleteProvisionalCard = computed(() => {
    if (record.value.id === undefined) return false;
    return record.value.canDeleteProvisionalCard === true;
  });

  const canChangeLostOrderStatus = computed(() => {
    if (record.value.provisionalOrderStatus === Enum.ProvisionalOrderStatusType.LOST.code) {
      return false;
    }
    return record.value.canChangeLostOrderStatus === true;
  });

  const canCancelLostOrderStatus = computed(() => {
    if (record.value.provisionalOrderStatus !== Enum.ProvisionalOrderStatusType.LOST.code) {
      return false;
    }
    return record.value.canCancelLostOrderStatus === true;
  });

  const canAddApplicant = computed(() => {
    if (record.value.id === undefined) return false;

    if (record.value.existsApplicantMyData) {
      return false;
    }

    return record.value.canAddApplicant === true;
  });

  const canRemoveApplicant = computed(() => {
    if (record.value.id === undefined) return false;

    if (!record.value.existsApplicantMyData) {
      return false;
    }
    return record.value.canRemoveApplicant === true;
  });

  const canConfirmProvisionalOrder = computed(() => {
    if (record.value.id === undefined) return false;

    return record.value.provisionalOrderStatus !== Enum.ProvisionalOrderStatusType.CONFIRMED.code;
  });

  /**
   * 積地初期値
   */
  const loadingDefault = () => {
    return {
      baseId: null,
      nameAndCd: null
    };
  };
  /**
   * 卸地初期値
   */
  const unloadingDefault = () => {
    return {
      baseId: null,
      nameAndCd: null,
      specifiedTimeFrom: null,
      specifiedTimeTo: null,
      unloadingOrder: 1
    };
  };

  /**
   * 積地フォームの追加
   */
  const addLoadingBase = () => {
    record.value.loadingBaseList.push(loadingDefault());
  };
  /**
   * 積地フォームの追加
   */
  const addUnloadingBase = () => {
    record.value.unloadingBaseList.push(unloadingDefault());
  };

  /**
   * 荷主枝番クリア
   */
  const clearClientBranch = () => {
    record.value.clientBranchId = null;
    record.value.clientBranchName = null;
  };

  /**
   * 積地フォームクリア
   */
  const clearLoadingBaseList = () => {
    record.value.loadingBaseList = [];
    addLoadingBase();
  };

  /**
   * 卸地フォームクリア
   */
  const clearUnloadingBaseList = () => {
    record.value.unloadingBaseList = [];
    addUnloadingBase();
  };

  /**
   * 荷主、荷主枝番、積地、卸地、車格
   * が全て選択されている場合に基本料金計算を実行する
   */
  const triggerCalcBasicPrice = async () => {
    const requestData = {
      clientId: record.value.clientId,
      clientBranchId: record.value.clientBranchId,
      loadingBaseIds: record.value.loadingBaseList
        .filter(v => v.baseId)
        .map(v => {
          return v.baseId;
        }),
      unloadingBaseIds: record.value.unloadingBaseList
        .filter(v => v.baseId)
        .map(v => {
          return v.baseId;
        }),
      vehicleTypeId: record.value.vehicleTypeId
    };

    // 上記のデータが全て存在しない場合は処理を終了
    for (const key in requestData) {
      if (requestData[key] === undefined || !requestData[key]) {
        return;
      }
    }

    // 積地・卸地が存在しない場合は処理を終了
    if (requestData.loadingBaseIds.length < 1 || requestData.unloadingBaseIds.length < 1) {
      return;
    }

    await store.dispatch("finance/fetchBillingCardBasicPrice", requestData);
  };

  const selectLoadingBase = (loading, data) => {
    triggerCalcBasicPrice();
  };

  const selectUnloadingBase = (unloading, data) => {
    unloading.specifiedTime = data.specifiedTime;
    triggerCalcBasicPrice();
  };

  const selectVehicleType = vehicleType => {
    triggerCalcBasicPrice();
  };

  const closeEditDialog = () => {
    record.value = initData();
    store.dispatch("finance/clearBillingCard");
  };

  const deleteLoadingBaseList = index => {
    record.value.loadingBaseList.splice(index, 1);
    triggerCalcBasicPrice();
  };

  const deleteUnloadingBaseList = index => {
    record.value.unloadingBaseList.splice(index, 1);
    triggerCalcBasicPrice();
  };

  const sortListById = list => {
    return list.sort((a, b) => {
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  };

  const initializeFormData = async values => {
    record.value = { ...initData(), ...values };

    setInitDate();
    if (!values || record.value.loadingBaseList == null) {
      clearLoadingBaseList();
    }
    if (!values || record.value.unloadingBaseList == null) {
      clearUnloadingBaseList();
    }
    if (values) {
      sortListById(record.value.loadingBaseList);
      sortListById(record.value.unloadingBaseList);
    }

    if (record.value.id) {
      await store.dispatch("finance/writeBillingCard", record.value.billingCard);
    } else {
      await store.dispatch("finance/fetchBillingCard", 0);
    }

    //仮請求カードをStoreに入れる
    await triggerCalcBasicPrice();
  };

  /**
   * 荷主変更時に、積地や卸地、荷主枝番をクリアする
   */
  watch(
    () => {
      try {
        return record.value && record.value.clientId ? record.value.clientId : null;
      } catch (e) {
        return null;
      }
    },
    (newValue, oldValue) => {
      if (!oldValue) {
        return;
      }

      clearClientBranch();

      if (isResetBaseTarget.value) {
        clearLoadingBaseList();
        clearUnloadingBaseList();
      }
    }
  );

  watch(
    () => {
      try {
        return record.value && record.value.clientBranchId ? record.value.clientBranchId : null;
      } catch (e) {
        return null;
      }
    },
    (newValue, oldValue) => {
      if (!oldValue) {
        return;
      }

      if (isResetBaseTarget.value) {
        clearLoadingBaseList();
        clearUnloadingBaseList();
      }
    }
  );

  const initData = () => {
    return {
      id: null,
      distanceType: null,
      vehicleCarryWeightId: null,
      handlingUnitType: null,
      vehicleQuantity: null,
      loadingBaseList: [],
      unloadingBaseList: [],
      itemTypeId: null,
      weight: null,
      orderDate: getInitOrderDate(),
      loadingDateFrom: getInitLoadingDate(),
      loadingDateTo: getInitLoadingDate(),
      unloadingDateFrom: getInitUnloadingDate(),
      unloadingDateTo: getInitUnloadingDate()
    };
  };

  /**
   * setTranData関数は、トランザクションデータを作成・整形・加工するために使用されます。
   *
   * - `store`から料金情報や基本料金情報を取得し、それらをトランザクションデータに追加します。
   * - オーダーの積み込み・積み下ろし情報を元に、整形されたリストを作成して返却するトランザクションデータに含めます。
   * - 重量情報が存在する場合、重量データを整形してセットします。
   *
   * @returns {Object} 整形されたトランザクションデータ
   */
  const setTranData = () => {
    const billingCard = store.getters["finance/billingCard"];
    const billingCardBasicPrice = store.getters["finance/billingCardBasicPrice"];

    const tranData = Object.assign({}, record.value);

    tranData["billingCard"] = { ...billingCard, ...{ billingCardBasicPrice: billingCardBasicPrice } };
    tranData["loadingBaseList"] = record.value.loadingBaseList?.map(value => {
      return {
        baseId: value.baseId
      };
    });
    tranData["unloadingBaseList"] = record.value.unloadingBaseList?.map(value => {
      return {
        baseId: value.baseId,
        specifiedTimeFrom: value.specifiedTimeFrom,
        specifiedTimeTo: value.specifiedTimeTo,
        unloadingOrder: value.unloadingOrder
      };
    });
    tranData["weight"] = record.value.weight ? String(record.value.weight).replace(/,/g, "") : null;

    return tranData;
  };

  /**
   * 積地・卸地の選択可否を示す変数。
   *
   * `record.value.client.id`または`record.value.clientBranch.id`のいずれかが未定義の場合、`false`を返す。
   * 両方が適切に定義されている場合に限り、`true`を返す。
   *
   * 荷主および荷主枝番が選択状態でないと積地卸地は選択不可という事
   */
  const canSelectBase = computed(() => {
    if (record.value.clientId === undefined && record.value.clientBranchId === undefined) {
      return false;
    }

    return record.value.clientId && record.value.clientBranchId;
  });

  return {
    initData,
    record,
    setTranData,
    canSelectBase,
    loadingBaseIdList,
    unloadingBaseIdList,
    loadingBaseLimit,
    unloadingBaseLimit,
    isDispatchedOrder,
    canDisplayBillingData,
    canEditData,
    isSettingKey,
    addLoadingBase,
    addUnloadingBase,
    clearLoadingBaseList,
    clearUnloadingBaseList,
    setInitDate,
    getInitOrderDate,
    getInitLoadingDate,
    getInitUnloadingDate,
    triggerCalcBasicPrice,
    selectLoadingBase,
    selectUnloadingBase,
    selectVehicleType,
    closeEditDialog,
    deleteLoadingBaseList,
    deleteUnloadingBaseList,
    initializeFormData,
    isLostOrderRegisterable,
    isLostOrderCancelable,
    canEditProvisionalOrderCard,
    canDeleteProvisionalCard,
    isDisplayChangeCreatingOrderStatus,
    isDisplayCancelCreatingOrderStatus,
    canChangeLostOrderStatus,
    canCancelLostOrderStatus,
    canAddApplicant,
    canRemoveApplicant,
    canConfirmProvisionalOrder
  };
}
